import React, { useEffect, useState } from "react";
import { Card, Row, Col, Statistic, Select, Spin } from "antd";
import {
  UserOutlined,
  FileTextOutlined,
  UserDeleteOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import PieChart from "src/components/charts/PieChart";
import BarChart from "src/components/charts/BarChart";
import { Container } from "src/components/container/Container";
import { GetQuizRulesListConfig } from "src/service/api";
import { QuizRules } from "src/static/interface";
import { useDashboardStatistics } from "./hooks/useDashboardStatistics";
import "src/styles/dashboard/index.scss";

const { Option } = Select;

function AdminDashboardPage() {
  // const [selectedUniversity, setSelectedUniversity] = useState<string>("all");
  const [selectedQuizRuleId, setSelectedQuizRuleId] = useState<number | null>(
    null
  );
  const [quizRules, setQuizRules] = useState<QuizRules[]>([]);

  // Custom hook dan barcha ma'lumotlarni olish
  const {
    loadingCharts,
    genderDistributionData,
    top10ByPercentage,
    bottom10ByPercentage,
    top10ByCompleted,
    bottom10ByCompleted,
    totalUsers,
    totalStudents,
    totalNotCompleted,
  } = useDashboardStatistics(selectedQuizRuleId);

  // Ishtirok darajasi foizini hisoblash
  const participationRate =
    totalStudents > 0
      ? Math.round((totalUsers / totalStudents) * 100 * 10) / 10
      : 0;

  useEffect(() => {
    // Quiz rules listini yuklash
    GetQuizRulesListConfig().then((res) => {
      setQuizRules(res.data || []);
      if (res.data && res.data.length > 0) {
        setSelectedQuizRuleId(32);
      }
    });
  }, []);

  const StatisticCard = ({
    title,
    value,
    icon,
    color,
    suffix = "",
  }: {
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    suffix?: string;
  }) => (
    <Card 
      className="dashboard__stat-card"
      style={{ 
        backgroundColor: `${color}15`,
        borderLeft: `4px solid ${color}`
      }}
    >
      <div className="dashboard__stat-content">
        <div
          className="dashboard__stat-icon"
          style={{ backgroundColor: color }}
        >
          {icon}
        </div>
        <div className="dashboard__stat-info">
          <Statistic
            title={title}
            value={value}
            suffix={suffix}
            valueStyle={{ color: color, fontSize: "24px", fontWeight: "600" }}
          />
        </div>
      </div>
    </Card>
  );

  return (
    <div>
      <Container
        title="Dashboard"
        extra={
          <div className="dashboard__filters">
            <Select
              value={selectedQuizRuleId}
              onChange={(val) => setSelectedQuizRuleId(val)}
              style={{ maxWidth: 700 }}
              placeholder="So'rovnomani tanlang"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {quizRules.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.theme}
                </Option>
              ))}
            </Select>
          </div>
        }
      >
        <Spin spinning={loadingCharts}>
          {/* Statistics Cards */}
          <div className="dashboard__stats">
            <StatisticCard
              title="Ishtirok etganlar soni"
              value={totalUsers}
              icon={<UserOutlined />}
              color="#3b82f6"
            />
            <StatisticCard
              title="Ishtirok etmaganlar soni"
              value={totalNotCompleted}
              icon={<UserDeleteOutlined />}
              color="#ef4444"
            />
            <StatisticCard
              title="Ishtirok darajasi (%)"
              value={participationRate}
              icon={<PercentageOutlined />}
              color="#8b5cf6"
              suffix="%"
            />
            <StatisticCard
              title="Jami so'rovnomalar soni"
              value={quizRules.length}
              icon={<FileTextOutlined />}
              color="#10b981"
            />
          </div>

          {/* University Participation Rate Row */}
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={12}>
              <Card
                title="📊 Top 10 - Talabalar va qatnashganlar"
                className="dashboard__chart-card"
              >
                <Spin spinning={loadingCharts}>
                  {top10ByCompleted.length > 0 ? (
                    <BarChart
                      data={[...top10ByCompleted].sort(
                        (a, b) => a.completed - b.completed
                      )}
                      height={450}
                      item={false}
                      bars={[
                        {
                          dataKey: "completed",
                          name: "Qatnashganlar",
                          color: "#43e97b",
                          gradientId: "topCompletedGradient",
                          animationDelay: 0,
                        },
                      ]}
                      animationDuration={1800}
                      layout="vertical"
                    />
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 0",
                        color: "#999",
                      }}
                    >
                      {selectedQuizRuleId
                        ? "Ma'lumotlar yuklanmoqda..."
                        : "So'rovnomani tanlang"}
                    </div>
                  )}
                </Spin>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                title="🏆 Top 10 - Qatnashish foizi bo'yicha"
                className="dashboard__chart-card"
              >
                <Spin spinning={loadingCharts}>
                  {top10ByPercentage.length > 0 ? (
                    <BarChart
                      data={[...top10ByPercentage].sort(
                        (a, b) => a.percentage - b.percentage
                      )}
                      height={450}
                      bars={[
                        {
                          dataKey: "percentage",
                          name: "Qatnashish foizi (%)",
                          color: "#52c41a",
                          gradientId: "topPercentageGradient",
                          animationDelay: 0,
                        },
                      ]}
                      animationDuration={1800}
                      layout="vertical"
                    />
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 0",
                        color: "#999",
                      }}
                    >
                      {selectedQuizRuleId
                        ? "Ma'lumotlar yuklanmoqda..."
                        : "So'rovnomani tanlang"}
                    </div>
                  )}
                </Spin>
              </Card>
            </Col>
          </Row>

          {/* University Statistics Row */}
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={12}>
              <Card
                title="📊 Eng kam 10 - Talabalar va qatnashganlar"
                className="dashboard__chart-card"
              >
                <Spin spinning={loadingCharts}>
                  {bottom10ByCompleted.length > 0 ? (
                    <BarChart
                      data={[...bottom10ByCompleted].sort(
                        (a, b) => a.completed - b.completed
                      )}
                      height={450}
                      bars={[
                        {
                          dataKey: "completed",
                          name: "Qatnashganlar",
                          color: "#ff6b6b",
                          gradientId: "bottomCompletedGradient",
                          animationDelay: 0,
                        },
                      ]}
                      animationDuration={1800}
                      layout="vertical"
                    />
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 0",
                        color: "#999",
                      }}
                    >
                      {selectedQuizRuleId
                        ? "Ma'lumotlar yuklanmoqda..."
                        : "So'rovnomani tanlang"}
                    </div>
                  )}
                </Spin>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                title="📉 Eng kam 10 - Qatnashish foizi bo'yicha"
                className="dashboard__chart-card"
              >
                <Spin spinning={loadingCharts}>
                  {bottom10ByPercentage.length > 0 ? (
                    <BarChart
                      data={[...bottom10ByPercentage].sort(
                        (a, b) => a.percentage - b.percentage
                      )}
                      height={450}
                      bars={[
                        {
                          dataKey: "percentage",
                          name: "Qatnashish foizi (%)",
                          color: "#ff4d4f",
                          gradientId: "bottomPercentageGradient",
                          animationDelay: 0,
                        },
                      ]}
                      animationDuration={1800}
                      layout="vertical"
                    />
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 0",
                        color: "#999",
                      }}
                    >
                      {selectedQuizRuleId
                        ? "Ma'lumotlar yuklanmoqda..."
                        : "So'rovnomani tanlang"}
                    </div>
                  )}
                </Spin>
              </Card>
            </Col>
          </Row>

          {/* Charts Row 1 */}
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={12}>
              <Card
                title="Jinsi bo'yicha taqsimot"
                className="dashboard__chart-card"
              >
                <Spin spinning={loadingCharts}>
                  {genderDistributionData.length > 0 ? (
                    <PieChart
                      data={genderDistributionData}
                      height={240}
                      chartType="donut"
                      outerRadius={85}
                      innerRadius={45}
                      showLegend={true}
                      legendPosition="bottom"
                    />
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 0",
                        color: "#999",
                      }}
                    >
                      {selectedQuizRuleId
                        ? "Ma'lumotlar yuklanmoqda..."
                        : "So'rovnomani tanlang"}
                    </div>
                  )}
                </Spin>
              </Card>
            </Col>
          </Row>
        </Spin>
      </Container>
    </div>
  );
}

export default AdminDashboardPage;
